// Organização de bancos em pastas — feature #13.
//
// Compartilha estado global pra:
//   - folderFilter:    pasta selecionada (null = todas / sem filtro)
//   - foldersData:     lista plana de pastas do aluno
//   - bankViewMode:    "cards" | "compact" | "list" (persiste em localStorage)
//
// UI:
//   - <BankFoldersToolbar/> — barra de chips + view-mode toggle + "Nova pasta"
//   - <MoveToFolderMenu bank/> — botão flutuante por banco pra mover/desafixar
//
// Backend já pronto (apps/api/src/functions/student-bank-folders.ts) +
// migration 0049 aplicada.

import { signal } from "@preact/signals";
import { useState, useEffect } from "preact/hooks";
import {
  FolderClosed,
  FolderOpen,
  FolderPlus,
  LayoutGrid,
  LayoutList,
  List,
  MoreVertical,
  Loader2,
  Search,
  X,
  Edit3,
  Trash2
} from "lucide-react";
import { call, ApiError } from "./api";
import { openAlert, openConfirm } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { loadBanks } from "./App";
import { DroppableFolder } from "./BanksDnd";

export interface FolderRow {
  id: string;
  name: string;
  parentId: string | null;
  color: string | null;
  icon: string | null;
  position: number;
  bankCount: number;
  createdAt: string;
  updatedAt: string;
}

export type BankViewMode = "cards" | "compact" | "list";

const VIEW_MODE_KEY = "student-bank-view-mode";

function readViewMode(): BankViewMode {
  if (typeof localStorage === "undefined") return "cards";
  const v = localStorage.getItem(VIEW_MODE_KEY);
  if (v === "compact" || v === "list" || v === "cards") return v;
  return "cards";
}

function writeViewMode(mode: BankViewMode) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(VIEW_MODE_KEY, mode);
  } catch {
    // quota
  }
}

// === Signals ==========================================================

export const folderFilter = signal<string | null | "no-folder">(null);
//                       null = "Todos"
//                       "no-folder" = bancos sem pasta (raiz)
//                       string = id da pasta
export const foldersData = signal<FolderRow[]>([]);
export const foldersPhase = signal<"idle" | "loading" | "ready" | "error">("idle");
export const bankViewMode = signal<BankViewMode>(readViewMode());

// Busca em bancos — case-insensitive, matcha nome + descrição.
// Vazio = sem filtro de busca (mostra todos).
export const bankSearchQuery = signal<string>("");

export function setBankViewMode(mode: BankViewMode) {
  bankViewMode.value = mode;
  writeViewMode(mode);
}

export async function loadFolders(opts: { silent?: boolean } = {}) {
  if (!opts.silent) foldersPhase.value = "loading";
  try {
    const res = await call<{ folders: FolderRow[] }>(
      "/api/student/bank-folders"
    );
    foldersData.value = res.folders;
    foldersPhase.value = "ready";
  } catch (err) {
    console.warn("[bank-folders] load falhou:", err);
    foldersPhase.value = "error";
  }
}

// === Toolbar ==========================================================

const DND_HINT_KEY = "banks-dnd-hint-seen-v1";

export function BankFoldersToolbar() {
  useEffect(() => {
    if (foldersPhase.value === "idle") void loadFolders();
  }, []);

  const [showNew, setShowNew] = useState(false);
  // Dica de descoberta da feature drag&drop. Dismissível, uma vez por device.
  // [[feedback-user-clarity-context]]
  const [showDndHint, setShowDndHint] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DND_HINT_KEY) !== "1";
    } catch {
      return true;
    }
  });
  function dismissDndHint() {
    setShowDndHint(false);
    try {
      localStorage.setItem(DND_HINT_KEY, "1");
    } catch {
      /* ignore */
    }
  }
  const folders = foldersData.value;
  const mode = bankViewMode.value;
  const active = folderFilter.value;

  return (
    <>
      <div class="flex flex-col gap-3">
        <BankSearchInput />
        {showDndHint && folders.length > 0 && (
          <div class="flex items-start gap-2 rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-[11px] leading-snug text-violet-700 dark:text-violet-300">
            <span class="mt-0.5">💡</span>
            <p class="flex-1">
              <strong>Dica:</strong> arraste um banco e solte numa pasta abaixo
              pra organizar. Funciona em qualquer dispositivo.
            </p>
            <button
              type="button"
              onClick={dismissDndHint}
              class="rounded-md px-1.5 py-0.5 text-[10px] font-bold text-violet-700 hover:bg-violet-500/15 dark:text-violet-300"
            >
              Entendi
            </button>
          </div>
        )}
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex flex-wrap items-center gap-1.5">
            <FolderChip
              label="Todos"
              active={active === null}
              onClick={() => (folderFilter.value = null)}
            />
            <DroppableFolder folderKey="no-folder">
              <FolderChip
                label="Sem pasta"
                active={active === "no-folder"}
                onClick={() => (folderFilter.value = "no-folder")}
                icon={FolderOpen}
              />
            </DroppableFolder>
            {folders.map((f) => (
              <DroppableFolder key={f.id} folderKey={f.id}>
                <FolderChip
                  label={f.name}
                  count={f.bankCount}
                  active={active === f.id}
                  onClick={() => (folderFilter.value = f.id)}
                  icon={FolderClosed}
                />
              </DroppableFolder>
            ))}
            <button
              type="button"
              onClick={() => setShowNew(true)}
              class="inline-flex items-center gap-1 rounded-full border border-dashed border-border bg-background px-3 py-1 text-[11px] font-medium text-muted-foreground hover:border-primary hover:text-primary"
            >
              <FolderPlus className="size-3.5" /> Nova pasta
            </button>
          </div>
          <ViewModeToggle mode={mode} onChange={setBankViewMode} />
        </div>
      </div>

      {showNew && (
        <NewFolderModal onClose={() => setShowNew(false)} />
      )}
    </>
  );
}

// Input de busca compartilhado: nome + descrição do banco. Client-side por
// enquanto. Pesquisa dentro de questões fica pra um endpoint dedicado quando
// houver demanda (precisa percorrer questoes_full).
export function BankSearchInput() {
  const q = bankSearchQuery.value;
  return (
    <div class="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="search"
        value={q}
        onInput={(e) =>
          (bankSearchQuery.value = (e.target as HTMLInputElement).value)
        }
        placeholder="Buscar banco por nome ou descrição…"
        aria-label="Buscar banco"
        class="w-full rounded-md border border-border bg-background py-2 pl-9 pr-9 text-sm outline-none focus:border-primary"
      />
      {q && (
        <button
          type="button"
          onClick={() => (bankSearchQuery.value = "")}
          aria-label="Limpar busca"
          title="Limpar busca"
          class="absolute right-2 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
}

function FolderChip({
  label,
  count,
  active,
  onClick,
  icon: Icon
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
  icon?: typeof FolderClosed;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
      }`}
    >
      {Icon && <Icon className="size-3.5" />}
      {label}
      {typeof count === "number" && count > 0 && (
        <span
          class={`rounded-full px-1.5 text-[9px] font-bold ${
            active
              ? "bg-primary-foreground text-primary"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function ViewModeToggle({
  mode,
  onChange
}: {
  mode: BankViewMode;
  onChange: (m: BankViewMode) => void;
}) {
  const opts: Array<{ key: BankViewMode; label: string; icon: typeof LayoutGrid }> = [
    { key: "cards", label: "Cards", icon: LayoutGrid },
    { key: "compact", label: "Compacto", icon: LayoutList },
    { key: "list", label: "Lista", icon: List }
  ];
  return (
    <div class="inline-flex shrink-0 items-center gap-0.5 rounded-md border border-border bg-card/60 p-0.5">
      {opts.map((o) => {
        const Icon = o.icon;
        const active = mode === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            title={o.label}
            aria-label={`Visualização ${o.label}`}
            class={`grid size-7 place-items-center rounded transition-colors ${
              active
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent"
            }`}
          >
            <Icon className="size-3.5" />
          </button>
        );
      })}
    </div>
  );
}

// === Move-to-folder per-card menu =====================================

export function MoveToFolderMenu({
  bankId,
  currentFolderId
}: {
  bankId: string;
  currentFolderId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function moveTo(folderId: string | null) {
    setBusy(true);
    try {
      await call("/api/student/bank-folders?action=move", {
        method: "POST",
        body: JSON.stringify({ bankIds: [bankId], folderId })
      });
      await Promise.all([loadFolders({ silent: true }), loadBanks({ silent: true })]);
      setOpen(false);
    } catch (err) {
      void openAlert({
        title: "Não consegui mover",
        message: friendlyError(err)
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <span class="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        title="Mover para pasta"
        aria-label="Mover para pasta"
        class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent"
        disabled={busy}
      >
        {busy ? <Loader2 className="size-3.5 animate-spin" /> : <MoreVertical className="size-3.5" />}
      </button>
      {open && (
        <div class="absolute right-0 top-8 z-30 min-w-[180px] rounded-md border border-border bg-popover p-1 shadow-lg">
          <div class="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Mover para
          </div>
          <button
            type="button"
            onClick={() => moveTo(null)}
            class={`block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-accent ${
              currentFolderId === null ? "bg-accent" : ""
            }`}
          >
            <FolderOpen className="mr-1 inline size-3" /> Sem pasta (raiz)
          </button>
          {foldersData.value.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => moveTo(f.id)}
              class={`block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-accent ${
                currentFolderId === f.id ? "bg-accent" : ""
              }`}
            >
              <FolderClosed className="mr-1 inline size-3" /> {f.name}
            </button>
          ))}
          {foldersData.value.length === 0 && (
            <p class="px-2 py-1.5 text-[10px] text-muted-foreground">
              Você ainda não criou pastas. Use o botão "Nova pasta" acima.
            </p>
          )}
        </div>
      )}
    </span>
  );
}

// === New / Edit folder modal ==========================================

function NewFolderModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) {
      setErr("Dê um nome pra pasta.");
      return;
    }
    setSubmitting(true);
    try {
      await call("/api/student/bank-folders", {
        method: "POST",
        body: JSON.stringify({ name: name.trim() })
      });
      await loadFolders({ silent: true });
      onClose();
    } catch (err) {
      setErr(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div class="w-full max-w-sm rounded-lg border border-border bg-card p-5 shadow-xl">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <FolderPlus className="size-5 text-primary" /> Nova pasta
          </h2>
          <button
            type="button"
            onClick={onClose}
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>
        <form onSubmit={submit} class="space-y-3">
          <label class="block">
            <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Nome da pasta
            </span>
            <input
              type="text"
              required
              maxLength={80}
              autoFocus
              value={name}
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
              placeholder="ex: Cálculo, ENEM 2024..."
              aria-label="Nome da pasta"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </label>
          {err && (
            <p class="text-xs text-destructive">{err}</p>
          )}
          <div class="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              class="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
            >
              {submitting && <Loader2 className="size-3.5 animate-spin" />}
              Criar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// === Renomear / apagar pasta =========================================

export function FolderActionsMenu({ folder }: { folder: FolderRow }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  async function remove() {
    setOpen(false);
    const ok = await openConfirm({
      title: "Apagar pasta?",
      message: `Bancos dentro de "${folder.name}" vão pra raiz (sem pasta). A pasta é apagada de vez. Tem certeza?`,
      confirmLabel: "Apagar pasta",
      danger: true
    });
    if (!ok) return;
    try {
      await call(`/api/student/bank-folders?id=${encodeURIComponent(folder.id)}`, {
        method: "DELETE"
      });
      if (folderFilter.value === folder.id) folderFilter.value = null;
      await Promise.all([loadFolders({ silent: true }), loadBanks({ silent: true })]);
    } catch (err) {
      void openAlert({
        title: "Não consegui apagar",
        message: friendlyError(err)
      });
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        aria-label={`Ações da pasta ${folder.name}`}
        class="ml-1 grid size-5 place-items-center rounded text-muted-foreground hover:bg-accent"
      >
        <MoreVertical className="size-3" />
      </button>
      {open && (
        <div class="absolute right-0 top-7 z-30 min-w-[160px] rounded-md border border-border bg-popover p-1 shadow-lg">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(true);
              setOpen(false);
            }}
            class="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-xs hover:bg-accent"
          >
            <Edit3 className="size-3" /> Renomear
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void remove();
            }}
            class="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-xs text-red-600 hover:bg-red-500/10"
          >
            <Trash2 className="size-3" /> Apagar pasta
          </button>
        </div>
      )}
      {editing && (
        <EditFolderModal
          folder={folder}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}

function EditFolderModal({
  folder,
  onClose
}: {
  folder: FolderRow;
  onClose: () => void;
}) {
  const [name, setName] = useState(folder.name);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) {
      setErr("Dê um nome pra pasta.");
      return;
    }
    setSubmitting(true);
    try {
      await call(`/api/student/bank-folders?id=${encodeURIComponent(folder.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ name: name.trim() })
      });
      await loadFolders({ silent: true });
      onClose();
    } catch (err) {
      setErr(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div class="w-full max-w-sm rounded-lg border border-border bg-card p-5 shadow-xl">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-base font-bold">Renomear pasta</h2>
          <button
            type="button"
            onClick={onClose}
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>
        <form onSubmit={submit} class="space-y-3">
          <input
            type="text"
            required
            maxLength={80}
            autoFocus
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            aria-label="Nome da pasta"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          />
          {err && <p class="text-xs text-destructive">{err}</p>}
          <div class="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              class="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
            >
              {submitting && <Loader2 className="size-3.5 animate-spin" />}
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
